/**
 * contratos-v2-store — hooks React Query da aba Contratos v2 (CL4).
 *
 * NOVO store (o legado `contratos-store.ts` é dos contratos R-IMPORT/Portal e
 * NÃO é tocado). Endpoints em /api/v1/clientes/:id/contratos-v2.
 *
 * Dinheiro em CENTAVOS. ZERO localStorage.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, hasToken } from './api';
import { normalizarFranquia } from './contratos/franquia-texto';
import type { FranquiaDoMes } from './contratos/franquia-tipos';

export type ContratoStatusV2 =
  | 'em_implantacao'
  | 'ativo'
  | 'suspenso'
  | 'aviso_previo'
  | 'encerrado';

export type MotivoEncerramento =
  | 'fim_vigencia'
  | 'rescisao_cliente'
  | 'rescisao_igm'
  | 'inadimplencia'
  | 'substituido'
  // Mig 766 (23/10/2026): não conta no churn nem na base (o contrato nunca foi carteira de verdade).
  | 'cadastro_engano';

export type FranquiaModo = 'numerico' | 'ilimitado' | 'nenhum';
export type CobrancaModo = 'por_chamado' | 'por_hora' | 'primeira_hora_demais';

export const STATUS_LABEL: Record<ContratoStatusV2, string> = {
  em_implantacao: 'Em implantação',
  ativo: 'Ativo',
  suspenso: 'Suspenso',
  aviso_previo: 'Em aviso prévio',
  encerrado: 'Encerrado',
};

/**
 * A cor da etiqueta de situação do contrato. 🔴 19/10/2026: saiu de `ContratosTab.tsx` para cá — a Carteira de clientes mostra a
 * mesma etiqueta da ficha (mesmas cores e textos), e uma segunda tabela de cores seria um segundo motor.
 */
export const STATUS_VARIANT: Record<ContratoStatusV2, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  ativo: 'success',
  em_implantacao: 'warning',
  suspenso: 'neutral',
  aviso_previo: 'info',
  encerrado: 'danger',
};

export const MOTIVO_LABEL: Record<MotivoEncerramento, string> = {
  fim_vigencia: 'fim de vigência',
  rescisao_cliente: 'rescisão pelo cliente',
  rescisao_igm: 'rescisão pela IGM',
  inadimplencia: 'inadimplência',
  substituido: 'substituído',
  cadastro_engano: 'cadastrado por engano',
};

export interface ContratoEscopoV2 {
  id: string;
  contratoId: string;
  /** LEGADO — espelha o primeiro de `subsistemas` (mig 482). */
  tipoEscopoId: string | null;
  tipoEscopoNome: string | null;
  /** CONTRATOS-2: um escopo pode ter VÁRIOS subsistemas. */
  subsistemas: Array<{ id: string; nome: string }>;
  corretivosModo: FranquiaModo;
  corretivosFranquia: number | null;
  preventivaPeriodicidadeId: string | null;
  preventivaPeriodicidadeNome: string | null;
  /** Meses com preventiva (1–12), da tabela filha (mig 480). */
  preventivaMeses: number[];
  /** Dias da semana do SEMANAL (1=seg…7=dom); null fora dele. */
  preventivaDiasSemana: number[] | null;
  slaId: string | null;
  slaNome: string | null;
  emergencialSlaId: string | null;
  emergencialSlaNome: string | null;
  emergenciaisModo: FranquiaModo;
  emergenciaisFranquia: number | null;
  corretivoExtraModo: CobrancaModo | null;
  corretivoExtraValorCentavos: number | null;
  corretivoExtraPrimeiraHoraCentavos: number | null;
  corretivoExtraDemaisCentavos: number | null;
  plantaoModo: CobrancaModo | null;
  plantaoValorCentavos: number | null;
  plantaoPrimeiraHoraCentavos: number | null;
  plantaoDemaisCentavos: number | null;
  observacoes: string | null;
  ordem: number;
}

export interface ContratoObservacaoV2 {
  id: string;
  contratoId: string;
  texto: string;
  autorId: string | null;
  autorNome: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface ContratoAlteracaoV2 {
  id: string;
  campo: string;
  valorDe: string | null;
  valorPara: string | null;
  autorId: string | null;
  autorNome: string | null;
  criadoEmIso: string;
  /** C4 (06/10/2026): a linha em linguagem de gente, montada no servidor. */
  frase: string;
}

export interface ContratoV2 {
  id: string;
  clienteId: string;
  /** LEGADO — o cadastro não pede mais; `rotulo` identifica (CONTRATOS-2). */
  titulo: string | null;
  rotulo: string;
  tipoId: string | null;
  tipoIds: string[];
  tiposNomes: string[];
  /** 22/10/2026 (mig 744): o valor mensal de cada escopo, pelo id do tipo (null = não preenchido). */
  valoresPorEscopo?: Record<string, number | null>;
  dataTerminoIso: string | null;
  tipoNome: string | null;
  escopoDetalhado: boolean;
  empresaParceiraId: string | null;
  empresaParceiraNome: string | null;
  empresaLabel: string;
  valorMensalCentavos: number;
  dataInicioIso: string | null;
  status: ContratoStatusV2;
  motivoEncerramento: MotivoEncerramento | null;
  encerradoEmIso: string | null;
  avisoPrevioFimIso: string | null;
  aniversarioMes: number | null;
  indiceReajusteId: string | null;
  indiceReajusteNome: string | null;
  slaId: string | null;
  slaNome: string | null;
  locacaoPrazoMeses: number | null;
  propostaOrigemId: string | null;
  propostaOrigemLabel: string | null;
  nasceuDoGanho: boolean;
  criadoPor: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  escopos: ContratoEscopoV2[];
  observacoes: ContratoObservacaoV2[];
  alteracoes: ContratoAlteracaoV2[];
  telefonePlantao?: string | null;
}

export interface EmpresasDoCliente {
  temIgm: boolean;
  parceiras: string[];
}

export interface ContratosResposta {
  contratos: ContratoV2[];
  empresas: EmpresasDoCliente;
}

export interface Catalogos {
  empresasParceiras: Array<{ id: string; nome: string }>;
  indicesReajuste: Array<{ id: string; nome: string }>;
  slas: Array<{ id: string; nome: string; horasUteis: number }>;
  tipos: Array<{ id: string; nome: string; escopoDetalhado: boolean }>;
  tiposEscopo: Array<{ id: string; nome: string }>;
  periodicidades: Array<{ id: string; nome: string }>;
}

export interface ContratoInput {
  /** LEGADO — não enviado pelo formulário desde CONTRATOS-2. */
  titulo?: string | null;
  /** ESCOPO-CONTRATO: um ou mais tipos (o servidor aceita tipoId escalar como compat). */
  tipoIds: string[];
  dataTerminoIso: string | null;
  tipoId: string | null;
  empresaParceiraId: string | null;
  valorMensalCentavos: number;
  dataInicioIso: string | null;
  aniversarioMes: number | null;
  indiceReajusteId: string | null;
  slaId: string | null;
  locacaoPrazoMeses: number | null;
  /** Plantão próprio do cliente (mig 573) — vazio herda o número da IGM. */
  telefonePlantao?: string | null;
  /** 22/10/2026 (mig 744): o valor de cada escopo, só quando há mais de um (todos ou nenhum; a soma bate com o valor). */
  valoresPorEscopo?: Record<string, number | null>;
}

export interface EscopoInput {
  /** LEGADO — o servidor espelha o 1º de `subsistemaIds` aqui. */
  tipoEscopoId: string | null;
  tipoEscopoNome: string | null;
  /** CONTRATOS-2: um ou mais subsistemas. */
  subsistemaIds: string[];
  corretivosModo: FranquiaModo;
  corretivosFranquia: number | null;
  preventivaPeriodicidadeId: string | null;
  /** Meses com preventiva (1=jan … 12=dez). PREVENTIVA-MESES (mig 480). */
  preventivaMeses: number[];
  /** Dias da semana do SEMANAL (1=seg … 7=dom); null fora do Semanal. */
  preventivaDiasSemana: number[] | null;
  slaId: string | null;
  /** SLA-CONTRATOS (20/08): SLA proprio do emergencial — opcional. */
  emergencialSlaId: string | null;
  emergenciaisModo: FranquiaModo;
  emergenciaisFranquia: number | null;
  corretivoExtraModo: CobrancaModo | null;
  corretivoExtraValorCentavos: number | null;
  corretivoExtraPrimeiraHoraCentavos: number | null;
  corretivoExtraDemaisCentavos: number | null;
  plantaoModo: CobrancaModo | null;
  plantaoValorCentavos: number | null;
  plantaoPrimeiraHoraCentavos: number | null;
  plantaoDemaisCentavos: number | null;
  observacoes: string | null;
  ordem?: number;
}

function base(clienteId: string): string {
  return `/api/v1/clientes/${encodeURIComponent(clienteId)}/contratos-v2`;
}
function chave(clienteId: string) {
  return ['v1', 'clientes', 'contratos-v2', clienteId] as const;
}

export function useContratosV2(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: chave(clienteId ?? ''),
    queryFn: () => api.get<ContratosResposta>(base(clienteId!)),
    enabled: hasToken() && !!clienteId,
  });
}

/**
 * C1/D2 (06/10/2026): a franquia de corretivas do MÊS CORRENTE, já recortada ao cliente. Leitura só;
 * resposta torta vira vazio (`normalizarFranquia`). A chave mora debaixo da do cliente: mexer num
 * contrato invalida as duas.
 */
export function useFranquiaDoMesDoCliente(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: [...chave(clienteId ?? ''), 'franquia-do-mes'] as const,
    queryFn: async (): Promise<FranquiaDoMes> =>
      normalizarFranquia(await api.get<unknown>(`${base(clienteId!)}/franquia-do-mes`)),
    enabled: hasToken() && !!clienteId,
    retry: false,
  });
}

export function useContratosCatalogos(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'clientes', 'contratos-v2', 'catalogos'] as const,
    queryFn: () => api.get<Catalogos>(`${base(clienteId!)}/catalogos`),
    enabled: hasToken() && !!clienteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useContratosV2Actions(clienteId: string) {
  const qc = useQueryClient();
  const b = base(clienteId);
  const invalidate = () => qc.invalidateQueries({ queryKey: chave(clienteId) });

  const criar = useMutation({
    mutationFn: (input: ContratoInput) =>
      api.post<{ id: string; avisoDuplicado: string | null }>(b, input),
    onSuccess: invalidate,
  });
  const editar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ContratoInput> }) =>
      api.put<{ ok: true }>(`${b}/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
  const mudarStatus = useMutation({
    mutationFn: ({
      id,
      status,
      motivo,
      avisoPrevioFimIso,
    }: {
      id: string;
      status: ContratoStatusV2;
      motivo?: MotivoEncerramento | null;
      avisoPrevioFimIso?: string | null;
    }) =>
      api.post<{ ok: true }>(`${b}/${encodeURIComponent(id)}/status`, {
        status,
        motivo: motivo ?? null,
        avisoPrevioFimIso: avisoPrevioFimIso ?? null,
      }),
    onSuccess: invalidate,
  });

  const addEscopo = useMutation({
    mutationFn: ({ contratoId, input }: { contratoId: string; input: EscopoInput }) =>
      api.post<{ id: string }>(`${b}/${encodeURIComponent(contratoId)}/escopos`, input),
    onSuccess: invalidate,
  });
  const editarEscopo = useMutation({
    mutationFn: ({
      contratoId,
      escopoId,
      input,
    }: {
      contratoId: string;
      escopoId: string;
      input: EscopoInput;
    }) =>
      api.put<{ ok: true }>(
        `${b}/${encodeURIComponent(contratoId)}/escopos/${encodeURIComponent(escopoId)}`,
        input,
      ),
    onSuccess: invalidate,
  });
  const removerEscopo = useMutation({
    mutationFn: ({ contratoId, escopoId }: { contratoId: string; escopoId: string }) =>
      api.delete<{ ok: true }>(
        `${b}/${encodeURIComponent(contratoId)}/escopos/${encodeURIComponent(escopoId)}`,
      ),
    onSuccess: invalidate,
  });

  const addObservacao = useMutation({
    mutationFn: ({ contratoId, texto }: { contratoId: string; texto: string }) =>
      api.post<{ id: string }>(`${b}/${encodeURIComponent(contratoId)}/observacoes`, { texto }),
    onSuccess: invalidate,
  });
  const editarObservacao = useMutation({
    mutationFn: ({
      contratoId,
      obsId,
      texto,
    }: {
      contratoId: string;
      obsId: string;
      texto: string;
    }) =>
      api.put<{ ok: true }>(
        `${b}/${encodeURIComponent(contratoId)}/observacoes/${encodeURIComponent(obsId)}`,
        { texto },
      ),
    onSuccess: invalidate,
  });
  const removerObservacao = useMutation({
    mutationFn: ({ contratoId, obsId }: { contratoId: string; obsId: string }) =>
      api.delete<{ ok: true }>(
        `${b}/${encodeURIComponent(contratoId)}/observacoes/${encodeURIComponent(obsId)}`,
      ),
    onSuccess: invalidate,
  });

  return {
    criar,
    editar,
    mudarStatus,
    addEscopo,
    editarEscopo,
    removerEscopo,
    addObservacao,
    editarObservacao,
    removerObservacao,
  };
}
